import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { materialsService } from '../services/materialsService';
import { recipesService } from '../services/recipesService';
import { analyticsService } from '../services/analyticsService';
import type {
  Material,
  Recipe,
  CreateMaterialInput,
  UpdateMaterialInput,
  CreateRecipeInput,
  UpdateRecipeInput,
  StockAdjustment,
  AnalyticsDateRange,
  MaterialWithStatus
} from '../types';

// ===== QUERY KEYS =====
export const inventoryKeys = {
  all: ['inventory'] as const,
  
  materials: () => [...inventoryKeys.all, 'materials'] as const,
  materialsList: (branchId: string) => [...inventoryKeys.materials(), 'list', branchId] as const,
  materialsDetail: (id: string) => [...inventoryKeys.materials(), 'detail', id] as const,
  materialsWithStatus: (branchId: string) => [...inventoryKeys.materials(), 'status', branchId] as const,
  materialsLowStock: (branchId: string) => [...inventoryKeys.materials(), 'low-stock', branchId] as const,
  materialsBySupplier: (supplierId: string) => [...inventoryKeys.materials(), 'supplier', supplierId] as const,
  materialCostHistory: (materialId: string) => [...inventoryKeys.materials(), 'cost-history', materialId] as const,
  
  recipes: () => [...inventoryKeys.all, 'recipes'] as const,
  recipesList: (branchId: string) => [...inventoryKeys.recipes(), 'list', branchId] as const,
  recipesDetail: (id: string) => [...inventoryKeys.recipes(), 'detail', id] as const,
  recipesByMaterial: (materialId: string) => [...inventoryKeys.recipes(), 'by-material', materialId] as const,
  recipeVersions: (recipeId: string) => [...inventoryKeys.recipes(), 'versions', recipeId] as const,
  recipeCost: (recipeId: string) => [...inventoryKeys.recipes(), 'cost', recipeId] as const,
  recipeAvailability: (recipeId: string, servings: number) => [...inventoryKeys.recipes(), 'availability', recipeId, servings] as const,
  
  analytics: () => [...inventoryKeys.all, 'analytics'] as const,
  financialMetrics: (branchId: string, dateRange: AnalyticsDateRange) => [...inventoryKeys.analytics(), 'financial', branchId, dateRange] as const,
  wasteMetrics: (branchId: string, dateRange: AnalyticsDateRange) => [...inventoryKeys.analytics(), 'waste', branchId, dateRange] as const,
  inventoryMetrics: (branchId: string, dateRange: AnalyticsDateRange) => [...inventoryKeys.analytics(), 'inventory', branchId, dateRange] as const,
  materialUsage: (branchId: string, dateRange: AnalyticsDateRange) => [...inventoryKeys.analytics(), 'usage', branchId, dateRange] as const,
  recipeCostAnalysis: (branchId: string) => [...inventoryKeys.analytics(), 'recipe-costs', branchId] as const,
  dashboardOverview: (branchId: string, dateRange: AnalyticsDateRange) => [...inventoryKeys.analytics(), 'dashboard', branchId, dateRange] as const,
  costTrends: (branchId: string, materialId: string, months: number) => [...inventoryKeys.analytics(), 'cost-trends', branchId, materialId, months] as const
};

// ===== MATERIALS HOOKS =====

/**
 * Get all materials for a branch
 */
export function useMaterials(branchId: string, includeInactive = false) {
  return useQuery({
    queryKey: inventoryKeys.materialsList(branchId),
    queryFn: () => materialsService.getMaterials(branchId, includeInactive),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!branchId
  });
}

/**
 * Get materials with stock status indicators
 */
export function useMaterialsWithStatus(branchId: string) {
  return useQuery({
    queryKey: inventoryKeys.materialsWithStatus(branchId),
    queryFn: () => materialsService.getMaterialsWithStatus(branchId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!branchId
  });
}

/**
 * Get a single material by ID
 */
export function useMaterial(materialId: string) {
  return useQuery({
    queryKey: inventoryKeys.materialsDetail(materialId),
    queryFn: () => materialsService.getMaterialById(materialId),
    staleTime: 1000 * 60 * 5,
    enabled: !!materialId
  });
}

/**
 * Get low stock materials
 */
export function useLowStockMaterials(branchId: string) {
  return useQuery({
    queryKey: inventoryKeys.materialsLowStock(branchId),
    queryFn: () => materialsService.getLowStockMaterials(branchId),
    staleTime: 1000 * 60, // 1 minute (more frequent updates for alerts)
    enabled: !!branchId
  });
}

/**
 * Get material cost history
 */
export function useMaterialCostHistory(materialId: string) {
  return useQuery({
    queryKey: inventoryKeys.materialCostHistory(materialId),
    queryFn: () => materialsService.getCostHistory(materialId),
    staleTime: 1000 * 60 * 30, // 30 minutes (historical data)
    enabled: !!materialId
  });
}

/**
 * Create a new material
 */
export function useCreateMaterial() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (input: CreateMaterialInput) => materialsService.createMaterial(input),
    onSuccess: (_, variables) => {
      // Invalidate all materials queries for this branch
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materialsList(variables.branchId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materialsWithStatus(variables.branchId) });
    }
  });
}

/**
 * Update a material
 */
export function useUpdateMaterial() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateMaterialInput }) =>
      materialsService.updateMaterial(id, input),
    onSuccess: (data) => {
      // Invalidate specific material
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materialsDetail(data.id) });
      // Invalidate all materials lists
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materials() });
    }
  });
}

/**
 * Update material cost
 */
export function useUpdateMaterialCost() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      materialId, 
      newCost, 
      reason, 
      changedBy 
    }: { 
      materialId: string; 
      newCost: number; 
      reason?: string; 
      changedBy?: string 
    }) => materialsService.updateMaterialCost(materialId, newCost, reason, changedBy),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materialsDetail(variables.materialId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materialCostHistory(variables.materialId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materials() });
    }
  });
}

/**
 * Delete a material
 */
export function useDeleteMaterial() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (materialId: string) => materialsService.deleteMaterial(materialId),
    onSuccess: () => {
      // Invalidate all materials queries
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materials() });
    }
  });
}

/**
 * Adjust material stock
 */
export function useAdjustStock() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (adjustment: StockAdjustment) => materialsService.adjustStock(adjustment),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materialsDetail(variables.materialId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materials() });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.analytics() });
    }
  });
}

/**
 * Restock material (purchase)
 */
export function useRestock() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      materialId,
      quantity,
      costPerUnit,
      supplierId,
      invoiceNumber,
      notes,
      performedBy
    }: {
      materialId: string;
      quantity: number;
      costPerUnit?: number;
      supplierId?: string;
      invoiceNumber?: string;
      notes?: string;
      performedBy?: string;
    }) => materialsService.restock(
      materialId,
      quantity,
      costPerUnit,
      supplierId,
      invoiceNumber,
      notes,
      performedBy
    ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materialsDetail(variables.materialId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.materials() });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.analytics() });
    }
  });
}

// ===== RECIPES HOOKS =====

/**
 * Get all recipes for a branch
 */
export function useRecipes(branchId: string, includeInactive = false) {
  return useQuery({
    queryKey: inventoryKeys.recipesList(branchId),
    queryFn: () => recipesService.getRecipes(branchId, includeInactive),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!branchId
  });
}

/**
 * Get a single recipe by ID
 */
export function useRecipe(recipeId: string) {
  return useQuery({
    queryKey: inventoryKeys.recipesDetail(recipeId),
    queryFn: () => recipesService.getRecipeById(recipeId),
    staleTime: 1000 * 60 * 5,
    enabled: !!recipeId
  });
}

/**
 * Get recipes using a specific material
 */
export function useRecipesByMaterial(materialId: string) {
  return useQuery({
    queryKey: inventoryKeys.recipesByMaterial(materialId),
    queryFn: () => recipesService.getRecipesByMaterial(materialId),
    staleTime: 1000 * 60 * 10,
    enabled: !!materialId
  });
}

/**
 * Get recipe version history
 */
export function useRecipeVersions(recipeId: string) {
  return useQuery({
    queryKey: inventoryKeys.recipeVersions(recipeId),
    queryFn: () => recipesService.getRecipeVersions(recipeId),
    staleTime: 1000 * 60 * 30,
    enabled: !!recipeId
  });
}

/**
 * Calculate recipe cost
 */
export function useRecipeCost(recipeId: string) {
  return useQuery({
    queryKey: inventoryKeys.recipeCost(recipeId),
    queryFn: () => recipesService.calculateRecipeCost(recipeId),
    staleTime: 1000 * 60 * 5,
    enabled: !!recipeId
  });
}

/**
 * Check recipe availability
 */
export function useRecipeAvailability(recipeId: string, servings = 1) {
  return useQuery({
    queryKey: inventoryKeys.recipeAvailability(recipeId, servings),
    queryFn: () => recipesService.checkRecipeAvailability(recipeId, servings),
    staleTime: 1000 * 60, // 1 minute
    enabled: !!recipeId
  });
}

/**
 * Create a new recipe
 */
export function useCreateRecipe() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (input: CreateRecipeInput) => recipesService.createRecipe(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.recipesList(variables.branchId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.analytics() });
    }
  });
}

/**
 * Update a recipe
 */
export function useUpdateRecipe() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      recipeId, 
      input, 
      updatedBy 
    }: { 
      recipeId: string; 
      input: UpdateRecipeInput; 
      updatedBy?: string 
    }) => recipesService.updateRecipe(recipeId, input, updatedBy),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.recipesDetail(data.id) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.recipes() });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.analytics() });
    }
  });
}

/**
 * Delete a recipe
 */
export function useDeleteRecipe() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ recipeId, deletedBy }: { recipeId: string; deletedBy?: string }) =>
      recipesService.deleteRecipe(recipeId, deletedBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.recipes() });
    }
  });
}

/**
 * Duplicate a recipe
 */
export function useDuplicateRecipe() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      recipeId, 
      newName, 
      createdBy 
    }: { 
      recipeId: string; 
      newName: string; 
      createdBy?: string 
    }) => recipesService.duplicateRecipe(recipeId, newName, createdBy),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.recipesList(data.branch_id) });
    }
  });
}

// ===== ANALYTICS HOOKS =====

/**
 * Get financial metrics
 */
export function useFinancialMetrics(branchId: string, dateRange: AnalyticsDateRange) {
  return useQuery({
    queryKey: inventoryKeys.financialMetrics(branchId, dateRange),
    queryFn: () => analyticsService.getFinancialMetrics(branchId, dateRange),
    staleTime: 1000 * 60 * 5,
    enabled: !!branchId && !!dateRange.startDate && !!dateRange.endDate
  });
}

/**
 * Get waste metrics
 */
export function useWasteMetrics(branchId: string, dateRange: AnalyticsDateRange) {
  return useQuery({
    queryKey: inventoryKeys.wasteMetrics(branchId, dateRange),
    queryFn: () => analyticsService.getWasteMetrics(branchId, dateRange),
    staleTime: 1000 * 60 * 5,
    enabled: !!branchId && !!dateRange.startDate && !!dateRange.endDate
  });
}

/**
 * Get inventory metrics
 */
export function useInventoryMetrics(branchId: string, dateRange: AnalyticsDateRange) {
  return useQuery({
    queryKey: inventoryKeys.inventoryMetrics(branchId, dateRange),
    queryFn: () => analyticsService.getInventoryMetrics(branchId, dateRange),
    staleTime: 1000 * 60, // More frequent updates for stock alerts
    enabled: !!branchId && !!dateRange.startDate && !!dateRange.endDate
  });
}

/**
 * Get material usage metrics
 */
export function useMaterialUsage(branchId: string, dateRange: AnalyticsDateRange) {
  return useQuery({
    queryKey: inventoryKeys.materialUsage(branchId, dateRange),
    queryFn: () => analyticsService.getMaterialUsage(branchId, dateRange),
    staleTime: 1000 * 60 * 10,
    enabled: !!branchId && !!dateRange.startDate && !!dateRange.endDate
  });
}

/**
 * Get recipe cost analysis
 */
export function useRecipeCostAnalysis(branchId: string) {
  return useQuery({
    queryKey: inventoryKeys.recipeCostAnalysis(branchId),
    queryFn: () => analyticsService.getRecipeCostAnalysis(branchId),
    staleTime: 1000 * 60 * 10,
    enabled: !!branchId
  });
}

/**
 * Get dashboard overview (all metrics)
 */
export function useDashboardOverview(branchId: string, dateRange: AnalyticsDateRange) {
  return useQuery({
    queryKey: inventoryKeys.dashboardOverview(branchId, dateRange),
    queryFn: () => analyticsService.getDashboardOverview(branchId, dateRange),
    staleTime: 1000 * 60 * 5,
    enabled: !!branchId && !!dateRange.startDate && !!dateRange.endDate
  });
}

/**
 * Get cost trends for a material
 */
export function useCostTrends(branchId: string, materialId: string, months = 6) {
  return useQuery({
    queryKey: inventoryKeys.costTrends(branchId, materialId, months),
    queryFn: () => analyticsService.getCostTrends(branchId, materialId, months),
    staleTime: 1000 * 60 * 30,
    enabled: !!branchId && !!materialId
  });
}

/**
 * Create inventory snapshot
 */
export function useCreateInventorySnapshot() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      branchId,
      snapshotDate,
      snapshotType
    }: {
      branchId: string;
      snapshotDate: Date;
      snapshotType: 'opening' | 'closing' | 'period_end';
    }) => analyticsService.createInventorySnapshot(branchId, snapshotDate, snapshotType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.analytics() });
    }
  });
}
